import type { CostingSource, SalesCostEstimateStatus } from '../types/inventory';

// Helpers compartidos de formato y presentación para las pantallas de costo/margen
// (SalesCostEstimatePanel, SalesCostReportPanel). Fase 13A-0D: extraídos para evitar
// duplicación; no cambian ningún cálculo, solo cómo se muestran los valores.

export function formatMoney(value: string, currency = 'MXN') {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency }).format(Number(value));
}

export function formatOptionalMoney(value: string | null) {
  return value === null ? 'No disponible' : formatMoney(value);
}

export function formatOptionalSignedMoney(value: string | null) {
  if (value === null) return 'No disponible';
  return `${Number(value) > 0 ? '+' : ''}${formatMoney(value)}`;
}

/**
 * Un COSTO UNITARIO, que casi siempre es un número muy pequeño: 435 pesos por una caja de 9 litros
 * son 0.0483 por mililitro. `formatMoney` usa dos decimales y lo mostraría como $0.05 — un 3% de
 * error que además no cuadra al multiplicar por la cantidad, y el dueño lo nota.
 *
 * Dos decimales como mínimo (para que $2.50 no se lea «$2.5») y cuatro como máximo, que es la
 * precisión con la que la base guarda los costos unitarios.
 */
export function formatUnitCost(value: number | string, currency = 'MXN') {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(Number(value));
}

export function formatQuantity(value: string) {
  return new Intl.NumberFormat('es-MX', { maximumFractionDigits: 4 }).format(Number(value));
}

const COSTING_SOURCE_LABELS: Record<CostingSource['type'], string> = {
  COSTING_DEFAULT_PRESENTATION: 'Principal',
  FALLBACK_PRESENTATION: 'Fallback',
  STOCK_AVERAGE_COST: 'Costo real',
  // MANUAL_COST: reservado para una futura fuente de costo manual; ningún endpoint la produce hoy.
  MANUAL_COST: 'Costo manual',
  MISSING_COST: 'Sin costo estimado',
  MISSING_REAL_COST: 'Sin costo real',
};

export function costingSourceLabel(type: CostingSource['type']) {
  return COSTING_SOURCE_LABELS[type];
}

export function CostingSources({ sources }: { sources: CostingSource[] }) {
  if (!sources.length) return null;
  return (
    <small className="costing-source-list">
      {sources.map((source) => (
        <span key={`${source.type}-${source.name}-${source.detail}`} title={source.detail || undefined}>
          {costingSourceLabel(source.type)}: {source.name || 'No disponible'}{source.detail ? ` · ${source.detail}` : ''}
        </span>
      ))}
    </small>
  );
}

export function RealCostBadge({ available }: { available: boolean }) {
  return (
    <span className={available ? 'real-cost-badge real-cost-badge--available' : 'real-cost-badge real-cost-badge--missing'}>
      {available ? 'Costo real disponible' : 'Sin costo real'}
    </span>
  );
}

const RECIPE_STATUS_LABELS: Record<SalesCostEstimateStatus, string> = {
  COSTED: 'Costeado',
  MISSING_RECIPE: 'Sin receta',
  INCOMPLETE_COST: 'Costo incompleto',
  UNKNOWN: 'Sin identificar',
};

export function RecipeStatusBadge({ status }: { status: SalesCostEstimateStatus }) {
  return <span className={`cost-badge cost-badge--${status.toLowerCase()}`}>{RECIPE_STATUS_LABELS[status]}</span>;
}
